/**
 * Shared NotificationPanel Component
 * 
 * Reusable notification panel used in both Dashboard and TeamCommsApp.
 * Shows unified notifications from Team Comms (posts, comments, replies, DMs)
 * and optionally onboarding notifications.
 * 
 * Features:
 * - Notification grouping/stacking (e.g., "Alex, Maria, and 3 others reacted to your post")
 * - Click-to-navigate with mark-as-read
 * - Mark all read
 * - Robust newest-first sorting regardless of date format
 */

import React from 'react';
import { Bell, UserPlus, UserCheck, GraduationCap, FileCheck, Mail, MessageSquare, CheckCheck, Heart, ArrowRight, AlertTriangle, Gift, Trophy, Users, Music } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '@/components/shared';
import { formatTimeAgo } from '../../utils/timeFormat';
import { stripHtml } from '../../utils/html';

/** Parse any created_at value to a numeric UTC timestamp. Returns 0 for invalid/missing dates. */
function toTimestamp(dateStr) {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

/**
 * Group notifications by type + target (post_id, comment_id, or conversation_id).
 * Only REACTIONS are grouped. Comments, replies, and DMs are always individual.
 * Onboarding notifications are never grouped.
 * 
 * Groups: post_reaction by post_id, comment_reaction by comment_id,
 *         message_reaction by conversation_id
 */
function groupNotifications(notifications) {
  const groups = [];
  const groupMap = new Map(); // key -> group index

  for (const n of notifications) {
    // Onboarding + super-admin notifications are never grouped
    if (n.source === 'onboarding') {
      groups.push({ notifications: [n], key: `onboarding-${n.id}` });
      continue;
    }
    if (n.source === 'super_admin') {
      groups.push({ notifications: [n], key: `super_admin-${n.id}` });
      continue;
    }

    // Only group reactions — everything else is individual
    let groupKey = null;
    if (n.type === 'post_reaction' && n.post_id) {
      groupKey = `post_reaction_${n.post_id}`;
    } else if (n.type === 'comment_reaction' && n.comment_id) {
      groupKey = `comment_reaction_${n.comment_id}`;
    } else if (n.type === 'message_reaction' && n.conversation_id) {
      groupKey = `message_reaction_${n.conversation_id}`;
    }

    // If no grouping key, treat as individual
    if (!groupKey) {
      groups.push({ notifications: [n], key: `single-${n.id}` });
      continue;
    }

    if (groupMap.has(groupKey)) {
      const idx = groupMap.get(groupKey);
      groups[idx].notifications.push(n);
    } else {
      groupMap.set(groupKey, groups.length);
      groups.push({ notifications: [n], key: groupKey });
    }
  }

  return groups;
}

/**
 * Build display text for a notification group
 */
function getGroupDisplay(group) {
  const items = group.notifications;
  const first = items[0];

  // Single notification — show as-is
  if (items.length === 1) {
    return {
      title: first.title,
      message: first.source === 'comms' && first.type === 'post'
        ? `New post: ${stripHtml(first.message)}`
        : stripHtml(first.message),
    };
  }

  // Multiple notifications in group
  const uniqueSenders = [...new Map(items.map(n => [n.sender_id, n.sender_name])).values()];
  const othersCount = uniqueSenders.length - 1;

  let title;
  if (uniqueSenders.length === 1) {
    title = uniqueSenders[0];
  } else if (uniqueSenders.length === 2) {
    title = `${uniqueSenders[0]} and ${uniqueSenders[1]}`;
  } else {
    title = `${uniqueSenders[0]} and ${othersCount} other${othersCount > 1 ? 's' : ''}`;
  }

  let message;
  const type = first.type;
  if (type === 'post_reaction') {
    message = `reacted to your post`;
  } else if (type === 'comment_reaction') {
    message = `reacted to your comment`;
  } else if (type === 'message_reaction') {
    message = `reacted to your message${items.length > 1 ? 's' : ''}`;
  } else {
    message = stripHtml(first.message);
  }

  return { title, message };
}

export function NotificationPanel({
  notifications = [],
  onboardingNotifications = [],
  superAdminNotifications = [],
  user,
  onNotificationClick,
  onMarkAllRead,
  onClose,
  excludeOnboarding = false,
  API_URL,
  activeConversationId = null,
  // Optional header title and empty-list title (Fix 7 G1, the Services bell).
  // The defaults keep every existing caller's panel exactly as it was.
  title = 'Notifications',
  emptyText = null,
  // The bell's own unread total. Optional: when a caller passes it, it is the
  // authoritative number (the server's count across every row, not just the
  // page this panel was handed) and it drives both the "N new" pill and whether
  // the Mark-all control is offered.
  unreadCount = null
}) {
  // Normalize super-admin platform alerts (team-bonus outcomes)
  const normalizedSuperAdmin = (superAdminNotifications || []).map(n => {
    const failed = n.type === 'team_bonus_failed';
    const detail = n.detail || {};
    return {
      id: n.id,
      source: 'super_admin',
      type: n.type,
      title: n.church_name || 'A church',
      message: (n.owner_email ? `${n.owner_email} · ` : '') + (failed
        ? `Earned the bonus but it couldn't be granted${detail.reason ? ` (${detail.reason})` : ''} — tap to grant`
        : `Earned the +30 day team bonus${detail.granted_by ? ' (granted manually)' : ''}`),
      church_id: n.church_id,
      owner_email: n.owner_email,
      is_read: n.is_read,
      created_at: n.created_at,
    };
  });

  // Normalize onboarding notifications (from MongoDB)
  const normalizedOnboarding = excludeOnboarding ? [] : onboardingNotifications.map(n => ({
    id: n.id,
    source: 'onboarding',
    type: n.type,
    title: n.title,
    message: n.message,
    candidate_id: n.candidate_id,
    is_read: n.read,
    created_at: n.created_at
  }));

  // Normalize team comms notifications
  const normalizedComms = (notifications || [])
    .filter(n => {
      if (n.sender_id === user?.id) return false;
      if (activeConversationId && (n.type === 'dm' || n.type === 'message_reaction') && n.conversation_id === activeConversationId) {
        return false;
      }
      return true;
    })
    .map(n => ({
      id: n.id,
      source: 'comms',
      type: n.type,
      title: n.sender_name,
      message: n.preview || n.content || 'New activity',
      sender_id: n.sender_id,
      sender_name: n.sender_name,
      sender_avatar: n.sender_avatar,
      post_id: n.post_id,
      comment_id: n.comment_id,
      conversation_id: n.conversation_id,
      message_id: n.message_id,
      workshop_url: n.workshop_url,
      shared_item_id: n.shared_item_id,
      shared_item_type: n.shared_item_type,
      shared_action: n.shared_action,
      shared_month: n.shared_month,
      shared_year: n.shared_year,
      service_id: n.service_id, assignment_id: n.assignment_id, service_date: n.service_date, service_action: n.service_action,
      ...(['service_request','service_response','service_updated','service_comment'].includes(n.type) && n.title ? { title: n.title } : {}),
      is_read: n.is_read,
      created_at: n.created_at
    }));

  // Merge, sort (newest first), then group
  const allSorted = [...normalizedSuperAdmin, ...normalizedOnboarding, ...normalizedComms]
    .sort((a, b) => toTimestamp(b.created_at) - toTimestamp(a.created_at));

  const grouped = groupNotifications(allSorted);

  // Sort groups by their newest notification (computed from all items, not just [0]), limit to 15 groups
  const sortedGroups = grouped
    .map(g => {
      // Compute newestAt from the MAX timestamp across ALL notifications in the group
      const newestAt = Math.max(...g.notifications.map(n => toTimestamp(n.created_at)));
      // Sort notifications within each group newest-first for consistent display
      const sortedNotifs = [...g.notifications].sort((a, b) => toTimestamp(b.created_at) - toTimestamp(a.created_at));
      return {
        ...g,
        notifications: sortedNotifs,
        newestAt,
        isRead: g.notifications.every(n => n.is_read),
        unreadCount: g.notifications.filter(n => !n.is_read).length,
      };
    })
    .sort((a, b) => b.newestAt - a.newestAt)
    .slice(0, 15);

  // Unread totals come from the full list this panel was handed (after its own
  // filters), never from the 15 groups it displays. Counting the window made the
  // Mark-all control hide itself exactly when there was a backlog to clear: with
  // 15+ newer read groups in front of older unread rows, the bell showed a number
  // and the panel offered no way to clear it. `unreadCount` wins when supplied;
  // Math.max keeps the pill from ever reading lower than the rows on screen.
  const listUnread = allSorted.filter(n => !n.is_read).length;
  const totalUnread = typeof unreadCount === 'number'
    ? Math.max(unreadCount, listUnread)
    : listUnread;

  const handleNotificationClick = async (group) => {
    if (onNotificationClick) {
      // Pass the first (newest) notification for navigation
      // Also pass all notification IDs so the parent can mark them all as read
      const primary = group.notifications[0];
      await onNotificationClick({
        ...primary,
        _groupIds: group.notifications.map(n => n.id),
        _groupCount: group.notifications.length
      });
    }
    if (onClose) {
      onClose();
    }
  };

  const handleMarkAllRead = async () => {
    if (onMarkAllRead) {
      await onMarkAllRead();
    }
  };

  return (
    <div data-ui="float" data-ui-panel="notif" className="pop-in fixed sm:absolute right-2 sm:right-0 top-[60px] sm:top-full sm:mt-2 w-[calc(100vw-16px)] sm:w-96 max-w-[384px] bg-card rounded-2xl shadow-2xl border border-slate-200/80 z-[100] overflow-hidden">
      {/* Header */}
      <div data-ui="notif-head" className="px-4 py-3 border-b border-slate-100 dark:border-white/10 flex items-center justify-between bg-slate-50/50 dark:bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <div data-ui="notif-bell" className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center">
            <Bell className="w-3.5 h-3.5 text-slate-600 dark:text-slate-300" />
          </div>
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {totalUnread > 0 && (
            <>
              <button
                onClick={handleMarkAllRead}
                data-ui="mark-all-btn"
                aria-label="Mark all as read"
                className="flex items-center justify-center gap-1.5 w-7 h-7 sm:w-auto sm:h-auto sm:px-2.5 sm:py-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-white/10 rounded-lg transition-colors"
                data-testid="mark-all-read-btn"
                title="Mark all as read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Mark all read</span>
              </button>
              <Badge data-ui="notif-count" className="bg-red-500 text-white text-xs font-medium px-2 py-0.5">
                {totalUnread} new
              </Badge>
            </>
          )}
        </div>
      </div>
      
      {/* Scrollable Content */}
      <div data-ui="notif-list" className="max-h-96 overflow-y-auto">
        {sortedGroups.length > 0 ? (
          <div className="py-2">
            {sortedGroups.map(group => {
              const primary = group.notifications[0];
              const display = getGroupDisplay(group);
              const isGrouped = group.notifications.length > 1;

              return (
                <div 
                  key={group.key}
                  data-ui="notif-row"
                  data-unread={!group.isRead}
                  className={`mx-2 mb-1 p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                    !group.isRead 
                      ? 'bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100/80' 
                      : 'hover:bg-slate-50 dark:hover:bg-white/5'
                  }`}
                  onClick={() => handleNotificationClick(group)}
                  data-testid={`notification-group-${group.key}`}
                >
                  <div className="flex gap-3">
                    {/* Icon / Avatar */}
                    {primary.source === 'super_admin' ? (
                      <div data-ui="notif-icon" className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        primary.type === 'team_bonus_failed'
                          ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400'
                          : 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400'
                      }`}>
                        {primary.type === 'team_bonus_failed' ? (
                          <AlertTriangle className="w-5 h-5" />
                        ) : (
                          <Gift className="w-5 h-5" />
                        )}
                      </div>
                    ) : primary.source === 'onboarding' ? (
                      <div data-ui="notif-icon" className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        primary.type === 'new_intake' 
                          ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                          : primary.type === 'training_complete'
                          ? 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400'
                          : primary.type === 'stage_change'
                          ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400'
                          : primary.type === 'member_joined'
                          ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400'
                          : 'bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400'
                      }`}>
                        {primary.type === 'new_intake' ? (
                          <UserPlus className="w-5 h-5" />
                        ) : primary.type === 'training_complete' ? (
                          <GraduationCap className="w-5 h-5" />
                        ) : primary.type === 'stage_change' ? (
                          <ArrowRight className="w-5 h-5" />
                        ) : primary.type === 'member_joined' ? (
                          <UserCheck className="w-5 h-5" />
                        ) : (
                          <FileCheck className="w-5 h-5" />
                        )}
                      </div>
                    ) : primary.type === 'workshop' ? (
                      <div data-ui="notif-icon" className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-5 h-5" />
                      </div>
                    ) : primary.type === 'planner_review' ? (
                      <div data-ui="notif-icon" className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
                        <Trophy className="w-5 h-5" />
                      </div>
                    ) : primary.type === 'shared_planner' ? (
                      <div data-ui="notif-icon" className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/50 text-violet-600 dark:text-violet-400 flex items-center justify-center flex-shrink-0" data-testid="shared-planner-notif-icon">
                        <Users className="w-5 h-5" />
                      </div>
                    ) : ['service_request','service_response','service_updated','service_comment'].includes(primary.type) ? (
                      <div data-ui="notif-icon" className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400 flex items-center justify-center flex-shrink-0" data-testid="services-notif-icon">
                        <Music className="w-5 h-5" />
                      </div>
                    ) : isGrouped ? (
                      // Grouped notification — show stacked avatars or group icon
                      <div className="relative w-10 h-10 flex-shrink-0">
                        {/* Grouped reactions always show heart icon */}
                        <div data-ui="notif-icon" className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400 flex items-center justify-center">
                          <Heart className="w-5 h-5" />
                        </div>
                        {/* Count badge on grouped icon */}
                        <span data-ui="notif-groupcount" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-slate-700 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                          {group.notifications.length}
                        </span>
                      </div>
                    ) : primary.sender_avatar ? (
                      <UserAvatar 
                        userId={primary.sender_id}
                        name={primary.sender_name}
                        avatarUrl={primary.sender_avatar}
                        size="md"
                        className="rounded-xl"
                      />
                    ) : (
                      <div data-ui="notif-icon" className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        primary.type === 'dm' 
                          ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400'
                          : primary.type === 'post'
                          ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400'
                          : 'bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400'
                      }`}>
                        {primary.type === 'dm' ? (
                          <Mail className="w-5 h-5" />
                        ) : (
                          <MessageSquare className="w-5 h-5" />
                        )}
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span data-ui="notif-title" className={`text-sm ${!group.isRead ? 'font-semibold text-slate-800 dark:text-slate-100' : 'font-medium text-slate-600 dark:text-slate-300'}`}>
                          {display.title}
                        </span>
                        {!group.isRead && (
                          <span data-ui="notif-dot" className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></span>
                        )}
                      </div>
                      <p data-ui="notif-msg" className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                        {display.message}
                      </p>
                      <p data-ui="notif-time" className="text-[11px] text-slate-400 dark:text-slate-500 mt-1.5 font-medium">
                        {primary.created_at && formatTimeAgo(primary.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="studio-empty-wrap py-12 text-center">
            <div className="studio-empty-tile w-14 h-14 rounded-2xl bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center mx-auto mb-3">
              <Bell className="studio-empty-icon w-7 h-7 text-slate-300" />
            </div>
            <p className="studio-empty-title text-sm font-medium text-slate-400 dark:text-slate-500">
              {totalUnread > 0 ? 'Nothing to show here' : (emptyText || 'No new notifications')}
            </p>
            <p className="studio-empty-body text-xs text-slate-300 mt-1">
              {totalUnread > 0
                ? `${totalUnread} unread ${totalUnread === 1 ? 'notification is' : 'notifications are'} outside this list — use Mark all read to clear them.`
                : 'You\u2019re all caught up!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Export a hook to get unread count for the bell badge
// activeConversationId: if provided, exclude DM notifications for this conversation (user is actively viewing it)
export function getUnreadCount(notifications = [], onboardingNotifications = [], userId, excludeOnboarding = false, activeConversationId = null) {
  const onboardingUnread = excludeOnboarding ? 0 : onboardingNotifications.filter(n => !n.read).length;
  const commsUnread = notifications.filter(n => {
    if (n.is_read) return false;
    if (n.sender_id === userId) return false;
    if (activeConversationId && (n.type === 'dm' || n.type === 'message_reaction') && n.conversation_id === activeConversationId) {
      return false;
    }
    return true;
  }).length;
  return onboardingUnread + commsUnread;
}
